import type { ProductSummaryDto } from "@hisense/shared";
import type { CsvColumn } from "@/lib/exportCsv";
import { majorUnits } from "@/domain/money";
import { formatMaterialSize, vnDateTime } from "@hisense/shared";

/**
 * Cột của tệp xuất danh mục sản phẩm — dùng chung cho Excel và CSV qua nút Xuất
 * chung (`ExportButton`).
 *
 * Trước 16/09/2026 trang Sản phẩm có hộp thoại xuất riêng, chỉ ra CSV, với vòng
 * nạp trang chép tay. Nay đi qua hộp thoại chung: có Excel, cùng một hành vi
 * với mọi màn hình danh sách, và một chỗ để sửa khi hành vi đổi.
 *
 * `canSeeCost` quyết định ba cột nhà cung cấp / mã hàng của NCC / giá vốn: màn
 * hình giấu thì tệp cũng giấu — không truyền cột là cách chặn duy nhất ở phía
 * tệp, máy chủ đã bỏ trống các trường đó cho tài khoản không có quyền.
 */
export function productExportColumns(t: (key: string) => string, canSeeCost: boolean, canSeeRef: boolean): CsvColumn<ProductSummaryDto>[] {
  return [
    { header: t("products.code"), value: (r) => r.productCode },
    // Mã phụ: 667 sản phẩm thuộc 155 nhóm DÙNG CHUNG mã sản phẩm và chỉ khác
    // nhau ở mã phụ — thiếu cột này thì ba cỡ dây rút ra ba dòng giống hệt
    // nhau trong Excel. Cùng lý do đã thêm cho tệp xuất PI.
    { header: t("common.subRef"), value: (r) => r.subRef },
    // Mã hàng của khách: chỉ người được thấy (YC01-L1, 07/10/2026) — máy chủ đã trả rỗng, bỏ luôn cột cho tệp gọn.
    ...(canSeeRef ? [{ header: t("productList.column_customerRef"), value: (r: ProductSummaryDto) => r.customerRef }] : []),
    { header: t("products.description"), value: (r) => r.description },
    // Bản tiếng Hoa có từ 13/09/2026 nhưng tệp xuất chưa từng có cột này (09/10/2026).
    { header: t("products.descriptionZh"), value: (r) => r.descriptionZh },
    { header: t("products.type"), value: (r) => r.type },
    { header: t("products.brand"), value: (r) => r.brandName },
    { header: t("common.color"), value: (r) => r.color },
    { header: t("products.colorZh"), value: (r) => r.colorZh },
    { header: t("common.size"), value: (r) => r.size },
    { header: t("products.unit"), value: (r) => r.unit },
    ...(canSeeCost
      ? [
          { header: t("products.supplier"), value: (r: ProductSummaryDto) => r.supplierName },
          { header: t("products.supplierProductRef"), value: (r: ProductSummaryDto) => r.supplierProductRef },
          { header: t("products.costPrice"), value: (r: ProductSummaryDto) => majorUnits(r.costUnitPrice, 4) },
        ]
      : []),
    { header: t("products.sellingPrice"), value: (r) => majorUnits(r.sellingPrice, 4) },
    { header: t("products.lowestPrice"), value: (r) => majorUnits(r.lowestPrice, 4) },
    { header: t("productList.column_dtdPrice"), value: (r) => majorUnits(r.dtdPrice, 4) },
    { header: t("productList.column_exwPrice"), value: (r) => majorUnits(r.exwPrice, 4) },
    { header: t("productList.column_underMoqPrice"), value: (r) => majorUnits(r.underMoqPrice, 4) },
    // Tiền tệ đứng riêng, không ghép vào ô tiền, để =SUM() còn chạy — một tệp
    // toàn số trần không nói được nó là USD hay HKD.
    { header: t("common.currency"), value: (r) => r.currency },
    { header: t("products.stock"), value: (r) => r.stockQuantity },
    { header: t("products.approval"), value: (r) => r.approvalStatus },
    { header: t("productList.column_upCharge"), value: (r) => majorUnits(r.upCharge, 4) },
    { header: t("productList.sampleLead"), value: (r) => r.sampleLeadTime },
    { header: t("productList.bulkLead"), value: (r) => r.bulkLeadTime },
    { header: t("productList.moq"), value: (r) => r.moq },
    { header: t("productList.mcq"), value: (r) => r.mcq },
    { header: t("productList.msq"), value: (r) => r.msq },
    { header: t("products.wireSize"), value: (r) => r.wireSize },
    { header: t("products.weightPer100"), value: (r) => r.weightPer100 },
    { header: t("products.weightUnit"), value: (r) => r.weightUnit },
    { header: t("products.materialType"), value: (r) => r.materialType },
    { header: t("products.materialWidth"), value: (r) => formatMaterialSize(r.materialWidth, r.materialWidthUnit) },
    { header: t("products.materialThickness"), value: (r) => formatMaterialSize(r.materialThickness, r.materialThicknessUnit) },
    { header: t("products.materialHardness"), value: (r) => r.materialHardness },
    { header: t("products.reorderPoint"), value: (r) => r.reorderPoint },
    { header: t("products.created"), value: (r) => vnDateTime(r.createdAt) },
    { header: t("common.remarks"), value: (r) => r.remarks },
    { header: t("products.storage"), value: (r) => r.storage },
    { header: t("productEditor.defaultBin"), value: (r) => r.location },
  ];
}
